/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-10-31 09:58:30                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-01-23 16:40:10                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Services
  * Components
  * Styles
  * Customer: customer details view
    * useEffect: Get customer data
    * useEffect: Set breadcrumb
    * contactCreate: Handle contact creation event
    * handleTabChange: Handle tab change
*/

/* Imports */
import { t } from "i18next";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { showApiErrorToast } from "../../services/api/error";
import { Typography, IconButton, Tooltip, Spinner, Badge } from "@material-tailwind/react";
import { HomeIcon, UserIcon, WrenchIcon, ClipboardDocumentCheckIcon, MapPinIcon, CheckIcon } from "@heroicons/react/24/solid";
import { PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { showOdooSuccessToast } from "../../components/toast/DismissibleToast";
/***/

/* Services */
import { api } from "../../services/api/api";
import { validator } from "../../services/form-validator/form-validator";
import { ContactFormValidator } from "../../components/data/contact/ContactForm";
import { isOdooConnected } from "../../services/odoo/odoo";
import { isRomaniaRealm } from "../../services/utils/utils";
/***/

/* Components */
import { Breadcrumb } from "../../components/breadcrumb/Breadcrumb";
import Loading from "../../components/loading/Loading";
import { TabsNav } from "../../components/tabs/TabsNav";
import { 
  InformationsTab, 
  ContactsTab, 
  MachinesTab, 
  InterventionsTab, 
  VisitsTab 
} from "./tabs";
import CustomerSendToOdooDialog from "../../components/data/customer/CustomerSendToOdooDialog";
/***/

/* Styles */
import "./Customer.scss";
/***/

// Composant interne pour gérer l'affichage du badge de statut (Succès/Erreur/Attention)
const ActionStatusBadge = ({
  success,
  error,
  warning,
  children
}: {
  success: boolean;
  error: boolean;
  warning?: boolean;
  children: React.ReactNode
}) => {
  if (!success && !error && !warning) return children;

  let badgeContent = <CheckIcon className="h-3.5 w-3.5 text-white" strokeWidth={3} />;
  let bgColor = "bg-[#209933]"; // Green

  if (error) {
    badgeContent = <span className="text-white text-[10px] font-bold">!</span>;
    bgColor = "bg-[#F50A0A]"; // Red
  } else if (warning) {
    badgeContent = <span className="text-white text-[10px] font-bold">!</span>;
    bgColor = "bg-[#F59E0B]"; // Orange
  }

  const badgeClass = `!min-w-[24px] !min-h-[24px] !w-[24px] !h-[24px] p-0 flex items-center justify-center ${bgColor}`;

  return (
    <Badge
      content={badgeContent}
      className={badgeClass}
      overlap="circular"
    >
      {children}
    </Badge>
  );
};

/**
* Customer: customer details view
* display details informations about customer
*/
export function Customer() {
  const { id } = useParams();
  const [customer, setCustomer] = useState<EasyTech.Customer>();
  const [path, setPath] = useState<{name: string, path: string}[]>([]);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("informations");
  const [showSendToOdooModal, setShowSendToOdooModal] = useState(false);
  const [sendToOdooLoading, setSendToOdooLoading] = useState(false);

  /**
  * useEffect: Get customer data
  * @param id - reload on customer id change 
  */
  const fetchCustomer = () => {
    api.get(`users/customers/${id}`)
      .then((ret) => setCustomer(ret as EasyTech.Customer))
      .catch((err) => {
        showApiErrorToast(err);
        navigate("/not-found");
      });
  };

  useEffect(() => {
    fetchCustomer();
  }, [id, navigate]);
  /***/

  /**
  * useEffect: Set breadcrumb
  * @param customer - reload on customer change
  */
  useEffect(() => { // Set breadcrumb path
    if (customer) {
      setPath([{
        name: t("common:customers"),
        path: "/customers"
      }, {
        name: `${customer.name}`,
        path: `/customers/${customer.id}`
      }]);
    }
  }, [customer]);
  /***/

  /**
   * confirmSendToOdoo: Handle customer sync to Odoo
   */
  const confirmSendToOdoo = async () => {
    try {
      setSendToOdooLoading(true);
      await api.post(`admin/customers/${id}/sync/odoo`, {});
      showOdooSuccessToast(t("client:odoo:success"));
      setShowSendToOdooModal(false);
      fetchCustomer(); // Refresh data to get externalId
    } catch (error) {
      console.error("Erreur lors de l envoi Odoo:", error);
      showApiErrorToast(error, { fallbackMessage: t("client:odoo:error") });
      setShowSendToOdooModal(false);
    } finally {
      setSendToOdooLoading(false);
    }
  };

  /**
  * contactCreate: Handle contact creation event 
  * @param data - contact to create
  */
  const contactCreate = (data: EasyTech.Contact): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (validator.isFormValid(data, ContactFormValidator)) {
        api.post("users/contacts", {...data, customer: {id: id}})
          .then((ret) => {
            toast.success("Contact created !");

            if (customer && customer.contacts) {
              setCustomer({...customer, contacts: [...customer.contacts, {...data, ret}]});
            }
            return resolve();
          }).catch((err) => {
            showApiErrorToast(err);
            return reject();
          });
      } else {
        const fieldNames = {
          firstName: t("common:firstName"),
          lastName: t("common:lastName"),
          function: t("common:function"),
          civility: t("common:civility"),
          contactInfo: t("common:contactInfo")
        };
        
        const errorMessage = validator.getFormErrorMessage(data, ContactFormValidator, fieldNames, t);
        toast.error(errorMessage);
        return reject();
      }
    });
  };
  /***/

  /**
   * contactUpdated: replace the edited contact in the loaded customer so the
   * contacts tab reflects the change without a full reload.
   */
  const contactUpdated = (updated: EasyTech.Contact) => {
    setCustomer((current) => {
      if (!current?.contacts) return current;
      return {
        ...current,
        contacts: current.contacts.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)),
      };
    });
  };

  /**
   * contactDeleted: drop the disabled contact from the loaded customer.
   * The contacts tab keeps its own list, but a tab change rebuilds that tab
   * from customer.contacts. Leaving the contact there brings it back until reload.
   */
  const contactDeleted = (contactId: string) => {
    setCustomer((current) => {
      if (!current?.contacts) return current;
      return {
        ...current,
        contacts: current.contacts.filter((c) => c.id !== contactId),
      };
    });
  };

  /**
   * handleTabChange: Handle tab change
   * @param value - new tab
   */
  const handleTabChange = (tabValue: string) => {
    setActiveTab(tabValue);
  };

  // Définition des onglets
  const tabs = [
    {
      label: t("client:tabs:informations"),
      value: "informations",
      icon: HomeIcon,
      Component: (props: { data: EasyTech.Customer }) => (
        <InformationsTab data={props.data} onChange={(customer) => setCustomer(customer)}/>
      ),
      disabled: false,
    },
    {
      label: t("common:contacts"),
      value: "contacts",
      icon: UserIcon,
      Component: (props: { data: EasyTech.Customer }) => (
        <ContactsTab data={props.data} onContactCreate={contactCreate} onContactUpdated={contactUpdated} onContactDeleted={contactDeleted} />
      ),
      disabled: false,
    },
    {
      label: t("common:machines"),
      value: "machines",
      icon: WrenchIcon,
      Component: MachinesTab,
      disabled: false,
    },
    {
      label: t("common:workorders"),
      value: "workorders",
      icon: ClipboardDocumentCheckIcon,
      Component: InterventionsTab,
      disabled: false,
    },
    {
      label: t("common:visits"),
      value: "visits",
      icon: MapPinIcon,
      Component: VisitsTab,
      disabled: false,
    },
  ];

  return (
    <div className="customer-details">
      {customer && customer.address ?
        <>
          <Breadcrumb path={path}/>

          <div className="flex justify-between items-center mb-4">
            <Typography variant="h2">
              {customer.name}
            </Typography>

            {isOdooConnected() && !isRomaniaRealm() && (
              <div className="flex gap-2">
                <Tooltip content={customer.externalId ? t("client:action:updateInOdoo", { defaultValue: t("client:action:sendToOdoo") }) : t("client:action:sendToOdoo")}>
                  <ActionStatusBadge success={!!customer.externalId} error={false}>
                    <IconButton
                      className="icon-button"
                      color="white"
                      style={{
                        backgroundColor: sendToOdooLoading ? "#F9FAFB" : "#FFFFFF",
                        border: `2px solid ${sendToOdooLoading ? "#D1D5DB" : "#F59E0B"}`,
                        boxShadow: sendToOdooLoading ? "none" : "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)"
                      }}
                      onClick={() => setShowSendToOdooModal(true)}
                      disabled={sendToOdooLoading}
                      aria-label="Send to Odoo"
                    >
                      {sendToOdooLoading ? (
                        <Spinner className="h-5 w-5" color="amber" />
                      ) : (
                        <PaperAirplaneIcon
                          className="icon -rotate-90"
                          style={{ color: sendToOdooLoading ? "#D1D5DB" : "#F59E0B" }}
                          strokeWidth={2}
                        />
                      )}
                    </IconButton>
                  </ActionStatusBadge>
                </Tooltip>
              </div>
            )}
          </div>

          <TabsNav
            defaultTab={activeTab}
            data={customer}
            tabs={tabs}
            onTabChange={handleTabChange}
          />

          <CustomerSendToOdooDialog
            open={showSendToOdooModal}
            customerName={customer.name}
            isUpdate={!!customer.externalId}
            onClose={() => setShowSendToOdooModal(false)}
            onConfirm={confirmSendToOdoo}
            loading={sendToOdooLoading}
          />
        </>
        :
        <Loading/>}
    </div>
  );
}
/***/
